import { AdyenCheckout, Card } from '@adyen/adyen-web'
import { useCallback, useEffect, useRef, useState } from 'react'

import { AdyenSessionsClient } from '@/api/adyen-sessions-client'
import { TransactionsClient } from '@/api/transactions-client'
import { useApplePay } from '@/hooks/use-apple-pay'
import { useGooglePay } from '@/hooks/use-google-pay'
import { AdyenCardComponentFieldType, AdyenCheckoutStatus, SessionType } from '@/lib/enums'
import { postToHost } from '@/lib/post-message'

import type { AdyenEnvironment, ErrorWithState, SessionResult } from '@/lib/types'

interface UseAdyenCheckoutOptions {
  internalApiHost: string
  authToken: string
  adyenClientKey: string
  adyenEnvironment: AdyenEnvironment
  sessionType: SessionType
  encodedSessionData: string
  /** Required for PAYMENT, ignored for TOKENIZATION. */
  transactionId?: string
  /**
   * The embedder's own declaration of its host, cross-checked against
   * `ancestorOrigins` before Apple Pay's `domainName` is trusted. Absent →
   * falls back to the browser-reported top-level host directly. See
   * `useApplePay` and specs/app/index.md::Apple Pay.
   */
  parentHost?: string
  /** Build-time toggle: capture cardholder name on the card form. */
  showCardholderName?: boolean
  /**
   * When set, the Adyen card form renders a billing address block in the
   * given mode (`'partial'` — postal code only; `'full'` — the complete
   * address block). Omitted/undefined leaves Adyen's default (no fields).
   */
  billingAddressMode?: 'partial' | 'full'
  /**
   * BCP-47 locale tag forwarded to Adyen's `checkoutConfig.locale`. Empty
   * or undefined falls back to `'en-US'` (preserves pre-plan default).
   * Unknown tags pass through verbatim — Adyen has its own fallback.
   */
  locale?: string
  /**
   * Invoked when the Adyen form's overall validity flips. The hook tracks
   * the last-known value and only fires on actual flips — not per-keystroke.
   * Used by `/hpp-session` to drive an out-of-iframe CTA's enabled state.
   */
  onValidityChange?: (isValid: boolean) => void
  /**
   * Fired once a Google Pay attempt's session result settles — the hook
   * drives status/outcome itself since no route call submits it.
   */
  onGooglePayCompleted?: (result: SessionResult) => void
  /** Fired when the shopper dismisses the Google Pay sheet. */
  onGooglePayCancelled?: () => void
  /** Fired on a non-cancellation Google Pay component error. */
  onGooglePayError?: (error: ErrorWithState) => void
  /** Fired synchronously when a Google Pay attempt begins — see `useGooglePay`'s `onSubmitAttempt`. */
  onGooglePaySubmitAttempt?: () => void
}

interface UseAdyenCheckoutReturn {
  status: AdyenCheckoutStatus
  /**
   * Mount the Adyen card form into the given element. Idempotent — calling it
   * twice with the same ref is a no-op after the first mount resolves.
   */
  mount: (el: HTMLElement) => Promise<void>
  /**
   * Submit the form. Resolves with `{ refusal: null }` on success or
   * `{ refusal: <code> }` on a refused payment; throws ErrorWithState for
   * invalid form state, HttpError/SessionConflictError/SessionClosedError
   * for claim failures, or any other thrown error.
   *
   * Bit-exact mirror of the webpack-era `AdyenCardProvider.submitSession()`
   * — including the documented hang risk if Adyen emits `onError` without
   * a subsequent `onPaymentFailed`. See specs/app/index.md::Decisions
   * 2026-05-13 step 11b.
   */
  submit: () => Promise<SessionResult>
  /** True once the mounted ApplePay component's `isAvailable()` resolves. */
  applePayAvailable: boolean
  /**
   * Mount the Adyen ApplePay component, reusing the checkout session `mount()`
   * already created. No-op when the merchant account doesn't offer Apple Pay
   * or `isAvailable()` rejects. See specs/app/index.md::Apple Pay.
   */
  mountApplePay: (el: HTMLElement) => Promise<void>
  /**
   * Submit via Apple Pay. Claim ordering intentionally differs from `submit()`
   * — see specs/app/index.md::Apple Pay.
   */
  submitApplePay: () => Promise<SessionResult>
  /** True once the mounted GooglePay component's `isAvailable()` resolves. */
  googlePayAvailable: boolean
  /**
   * Mount the Adyen GooglePay component, reusing the checkout session `mount()`
   * already created. No-op when neither tx variant is offered or `isAvailable()`
   * rejects. See specs/app/index.md::Apple Pay context and T2 (JET-2262).
   */
  mountGooglePay: (el: HTMLElement) => Promise<void>
}

/**
 * Owns the Adyen SDK 6 wiring + the session claim/submit/result chain.
 *
 * Side state tracked across Adyen callbacks (matches the webpack-era class
 * `AdyenCardProvider`):
 *   - brand (from onBrand)
 *   - endDigits / encryptedExpiryDate / encryptedSecurityCode (from onFieldValid)
 *   - sessionId (from createAdyenSession / createAdyenTokenizationSession)
 *   - sessionResult promise (resolved by onPaymentCompleted / onPaymentFailed)
 *
 * Cross-frame messages emitted **from inside this hook** (per the dispatch
 * contract — specs/app/index.md::Decisions::2026-05-13):
 *   - 11a. `card-payment-failed` — from Adyen `onPaymentFailed`, carrying the
 *     additive `resultCode` field.
 *   - 11b. `card-payment-failed` — from Adyen `onError`. **Does not resolve
 *     the promise.** No `resultCode`: an `AdyenCheckoutError` has none.
 *   - 12.  `card-tokenization-completed` — from Adyen `onPaymentCompleted`
 *     for tokenization sessions.
 *
 * Messages from the **calling route** (not this hook):
 *   - `card-payment-setup` — after `mount` resolves.
 *   - 10a/b/c/d outcomes from `submit()`'s resolve/throw branches.
 */
export function useAdyenCheckout(options: UseAdyenCheckoutOptions): UseAdyenCheckoutReturn {
  const [status, setStatus] = useState<AdyenCheckoutStatus>(AdyenCheckoutStatus.INITIAL)
  const statusRef = useRef<AdyenCheckoutStatus>(AdyenCheckoutStatus.INITIAL)
  statusRef.current = status

  // Refs — survive re-renders and React Strict Mode double-invocation.
  const componentRef = useRef<unknown>(null)
  const sessionIdRef = useRef<string | null>(null)
  const sessionResultRef = useRef<SessionResult | null>(null)
  const sessionResultPromiseRef = useRef<Promise<SessionResult> | null>(null)
  const resolveSessionResultRef = useRef<((value: SessionResult) => void) | null>(null)
  const brandRef = useRef<string | undefined>(undefined)
  const endDigitsRef = useRef<string | undefined>(undefined)
  const mountedElRef = useRef<HTMLElement | null>(null)
  const mountInFlightRef = useRef<Promise<void> | null>(null)
  const lastValidityRef = useRef<boolean | null>(null)
  const rejectSessionResultRef = useRef<((error: Error) => void) | null>(null)

  const checkoutRef = useRef<unknown>(null)

  const adyenClientRef = useRef<AdyenSessionsClient | null>(null)
  const transactionsClientRef = useRef<TransactionsClient | null>(null)

  // Initialize the API clients lazily on first mount call.
  const ensureClients = useCallback(() => {
    if (!adyenClientRef.current) {
      adyenClientRef.current = new AdyenSessionsClient(options.internalApiHost, options.authToken)
    }
    if (!transactionsClientRef.current) {
      transactionsClientRef.current = new TransactionsClient(options.internalApiHost, options.authToken)
    }
  }, [options.internalApiHost, options.authToken])

  // Initialize the session result promise on first mount.
  const ensureSessionResultPromise = useCallback(() => {
    if (!sessionResultPromiseRef.current) {
      sessionResultPromiseRef.current = new Promise<SessionResult>((resolve, reject) => {
        resolveSessionResultRef.current = resolve
        rejectSessionResultRef.current = reject
      })
    }
  }, [])

  const resolveSessionResult = useCallback((result: SessionResult) => {
    sessionResultRef.current = result
    resolveSessionResultRef.current?.(result)
  }, [])

  const rejectSessionResult = useCallback((error: Error) => {
    // Idempotency — mirror the existing resolve/terminal-callback guards.
    if (sessionResultRef.current) return
    // Internal sentinel so subsequent `sessionResultRef.current`-guarded
    // callbacks no-op. The route never observes this — submit() awaits a
    // rejected promise, so the resolve branch is skipped entirely.
    sessionResultRef.current = { customerCreditCardId: null, refusal: 'post-payment-error' }
    rejectSessionResultRef.current?.(error)
  }, [])

  const waitForSessionResult = useCallback(() => {
    ensureSessionResultPromise()
    const sessionResultPromise = sessionResultPromiseRef.current
    if (!sessionResultPromise) {
      throw new Error('useAdyenCheckout.waitForSessionResult: session result promise missing')
    }
    return sessionResultPromise
  }, [ensureSessionResultPromise])

  // Read-only accessors shared with useApplePay — see specs/app/index.md::Decisions.
  const getCheckout = useCallback(() => checkoutRef.current, [])
  const getSessionId = useCallback(() => sessionIdRef.current, [])
  // Read-only accessor for useGooglePay's shared-lock guard — see specs/app/index.md::Google Pay.
  const getStatus = useCallback(() => statusRef.current, [])
  const claimSession = useCallback((sessionId: string) => {
    const adyenClient = adyenClientRef.current
    if (!adyenClient) {
      throw new Error('useAdyenCheckout.claimSession: adyen client not initialized')
    }
    return adyenClient.claimSession(sessionId)
  }, [])

  // Writes the shared brand/endDigits refs both wallets' onAuthorized populate — see specs/app/index.md::Apple Pay.
  const setWalletBrandInfo = useCallback((brand: string, endDigits: string) => {
    brandRef.current = brand
    endDigitsRef.current = endDigits
  }, [])

  const {
    applePayAvailable,
    mountApplePay,
    submitApplePay,
    getPendingClaim: getPendingApplePayClaim,
  } = useApplePay({
    getCheckout,
    getSessionId,
    claimSession,
    waitForSessionResult,
    setStatus,
    setWalletBrandInfo,
    adyenEnvironment: options.adyenEnvironment,
    parentHost: options.parentHost,
  })

  const {
    googlePayAvailable,
    mountGooglePay,
    getPendingClaim: getPendingGooglePayClaim,
  } = useGooglePay({
    getCheckout,
    getSessionId,
    getStatus,
    claimSession,
    waitForSessionResult,
    setStatus,
    setWalletBrandInfo,
    onCompleted: options.onGooglePayCompleted,
    onCancelled: options.onGooglePayCancelled,
    onError: options.onGooglePayError,
    onSubmitAttempt: options.onGooglePaySubmitAttempt,
  })

  // At most one wallet claim is ever in flight — Card and both wallets are mutually exclusive on the same session.
  const getPendingClaim = useCallback(
    () => getPendingApplePayClaim() ?? getPendingGooglePayClaim(),
    [getPendingApplePayClaim, getPendingGooglePayClaim]
  )

  // ----- Adyen callbacks -----

  // `data` stays required deliberately: the SDK declares onPaymentCompleted's
  // data non-optional (a completion always carries its result), unlike
  // onPaymentFailed's. `component` is optional on both, so its call is guarded.
  const onPaymentCompletedPayment = useCallback(
    async (data: { resultCode: string }, component?: { unmount: () => void }) => {
      if (sessionResultRef.current) return // idempotent

      const transactionId = options.transactionId
      if (!transactionId) {
        // Shouldn't happen for PAYMENT; bail to fail-safe.
        resolveSessionResult({ customerCreditCardId: null, refusal: 'missing-transaction-id' })
        component?.unmount()
        return
      }

      const sessionId = sessionIdRef.current
      if (!sessionId) return

      const transactionsClient = transactionsClientRef.current
      const adyenClient = adyenClientRef.current
      const brand = brandRef.current
      const endDigits = endDigitsRef.current
      // Defensive: by the time Adyen fires onPaymentCompleted, mount() has run
      // ensureClients() and the onBrand / onFieldValid callbacks have populated
      // brand and endDigits. If any are missing the session is structurally
      // broken — fail the result instead of crashing on a null deref.
      if (!transactionsClient || !adyenClient || !brand || !endDigits) {
        resolveSessionResult({ customerCreditCardId: null, refusal: 'refs-not-initialized' })
        component?.unmount()
        return
      }

      try {
        // Apple Pay's concurrently-fired claim is checked here; a 409/410
        // throws into the catch below. Null on the Card path (its claim
        // settled before submit). See specs/app/index.md::Apple Pay.
        const pendingApplePayClaim = getPendingClaim()
        if (pendingApplePayClaim) {
          await pendingApplePayClaim
        }
        const contact = await transactionsClient.getContact(transactionId)
        const sessionResultResponse = await adyenClient.sendAdyenSessionResult({
          brand,
          endDigits,
          contactId: contact.identifier,
          transactionId,
          resultCode: data.resultCode,
          sessionId,
        })

        const customerCreditCardId = sessionResultResponse.customer_credit_card_id
        if (customerCreditCardId) {
          await transactionsClient.payTransactionViaCreditCard(transactionId, customerCreditCardId)
        }

        resolveSessionResult({ customerCreditCardId, refusal: null })
      } catch (err) {
        // Adyen authorized — onPaymentCompleted only fires on a successful PSP
        // outcome. Failures here are internal-API / GraphQL / transport, not
        // card refusals. Reject the session-result promise with
        // state.isValid: true so submit() throws an ErrorWithState into the
        // route's catch branch 10c, which emits card-payment-error
        // (dispatch-order contract — specs/app/index.md::Decisions
        // 2026-05-13 step 10c, made reachable by this plan).
        const error = err instanceof Error ? err : new Error(String(err))
        const errorWithState: ErrorWithState = Object.assign(error, { state: { isValid: true } })
        console.error('useAdyenCheckout.onPaymentCompletedPayment: post-payment chain failed', errorWithState)
        rejectSessionResult(errorWithState)
      } finally {
        component?.unmount()
      }
    },
    [options.transactionId, resolveSessionResult, rejectSessionResult, getPendingClaim]
  )

  const onPaymentCompletedTokenization = useCallback(
    async (_data: { resultCode: string }, component?: { unmount: () => void }) => {
      if (sessionResultRef.current) return // idempotent
      resolveSessionResult({ customerCreditCardId: null, refusal: null })
      component?.unmount()
      postToHost('card-tokenization-completed')
    },
    [resolveSessionResult]
  )

  /**
   * Adyen settled the payment on a non-authorised final code — `Refused`,
   * `Cancelled`, or `Error` (see `AdyenPaymentResultCode`). The code rides
   * along as an additive field on the outbound message so a host can tell a
   * shopper who cancelled from a card the issuer declined; unlisted codes are
   * forwarded raw, since Adyen owns its own vocabulary. `resultCode` is set on
   * the session result too, so the route's own emission (step 10a) agrees with
   * this one — a single refused flow emits `card-payment-failed` twice.
   */
  const onPaymentFailed = useCallback(
    async (data?: { resultCode?: string }, component?: { unmount: () => void }) => {
      if (sessionResultRef.current) return

      // Adyen declares both arguments optional (SDK 6.16.0) — unlike
      // onPaymentCompleted, a failure can arrive with no data at all (expired
      // session, abandoned flow). `refusal` must stay truthy or the route's
      // `if (result.refusal)` branch would report this failed payment as
      // completed; the synthetic fallback never leaves the iframe, since only
      // `resultCode` crosses to the host and it stays absent here.
      const resultCode = data?.resultCode
      resolveSessionResult({ customerCreditCardId: null, refusal: resultCode ?? 'unknown', resultCode })
      component?.unmount()
      postToHost('card-payment-failed', resultCode ? { resultCode } : undefined)
    },
    [resolveSessionResult]
  )

  const onError = useCallback((error: Error, component?: { unmount: () => void }) => {
    // NOTE: matches the webpack-era getOnErrorCallback — emits the message and
    // unmounts but **does NOT** resolve sessionResultPromise. If Adyen emits
    // onError without a subsequent onPaymentFailed, submit() will hang. This
    // is the documented bug in specs/app/index.md::Decisions::2026-05-13 step
    // 11b, preserved bit-exact pending a dedicated fix. `component` is optional
    // per the SDK, so the unmount is guarded.
    console.error(error.name, error.message, error.stack, component)
    component?.unmount()
    postToHost('card-payment-failed')
  }, [])

  const onFieldValid = useCallback(
    async (data: { fieldType: string; valid?: boolean; endDigits?: string; blob?: string }) => {
      if (!data.valid) return
      switch (data.fieldType) {
        case AdyenCardComponentFieldType.ENCRYPTED_CARD_NUMBER:
          endDigitsRef.current = data.endDigits
          break
        case AdyenCardComponentFieldType.ENCRYPTED_EXPIRY_DATE:
        case AdyenCardComponentFieldType.ENCRYPTED_SECURITY_CODE:
          // Encrypted blob is tracked by the SDK internally; reading it here
          // is only useful for the (deprecated) raw-card-details path. The
          // webpack-era code stored these fields as instance state but they
          // are not used downstream after the SDK 6 refactor.
          break
      }
    },
    []
  )

  const onBrand = useCallback(async (data: { brand: string }) => {
    brandRef.current = data.brand
  }, [])

  const onChange = useCallback(
    (state: { isValid?: boolean }) => {
      // Terminal-state gate — match the mount guard. After a payment settles
      // the SDK may still fire tear-down callbacks; those don't represent
      // real user-driven validity changes.
      if (sessionResultRef.current) return
      const isValid = !!state.isValid
      if (lastValidityRef.current === isValid) return
      lastValidityRef.current = isValid
      options.onValidityChange?.(isValid)
    },
    [options.onValidityChange]
  )

  // ----- mount -----

  const mount = useCallback(
    async (el: HTMLElement) => {
      // Idempotent: if a mount is in flight, return the same promise.
      if (mountInFlightRef.current) return mountInFlightRef.current
      // Session already settled (payment completed, failed via callback, or
      // tokenization done) — never re-mount the Adyen widget over a finished
      // session. Without this guard, the AdyenDropin's useEffect re-firing
      // (e.g., from a status change downstream) would create a brand-new
      // session and mount a fresh form on top of the closed flow.
      if (sessionResultRef.current) return
      // Same element + already mounted = no-op.
      if (mountedElRef.current === el) return

      ensureClients()
      ensureSessionResultPromise()

      mountInFlightRef.current = (async () => {
        setStatus(AdyenCheckoutStatus.PREPARING)

        const adyenClient = adyenClientRef.current
        if (!adyenClient) {
          // ensureClients() ran two lines up; the only way to land here is a
          // race that should not happen with refs.
          throw new Error('useAdyenCheckout.mount: adyen client not initialized')
        }
        const isTokenization = options.sessionType === SessionType.TOKENIZATION

        const sessionResponse = isTokenization
          ? await adyenClient.createAdyenTokenizationSession(options.encodedSessionData)
          : await adyenClient.createAdyenSession(options.encodedSessionData)

        sessionIdRef.current = sessionResponse.id

        const checkoutConfig = {
          clientKey: options.adyenClientKey,
          environment: options.adyenEnvironment,
          locale: options.locale || 'en-US',
          session: { id: sessionResponse.id, sessionData: sessionResponse.session_data },
          onPaymentCompleted: isTokenization ? onPaymentCompletedTokenization : onPaymentCompletedPayment,
          onPaymentFailed,
          onError,
          onChange,
          showPayButton: false,
        }

        const cardConfig = {
          ...(options.showCardholderName
            ? {
                hasHolderName: true,
                holderNameRequired: true,
                autoFocus: true,
                positionHolderNameOnTop: true,
              }
            : {}),
          ...(options.billingAddressMode
            ? {
                billingAddressRequired: true,
                billingAddressMode: options.billingAddressMode,
              }
            : {}),
          onFieldValid,
          onBrand,
        }

        // biome-ignore lint/suspicious/noExplicitAny: Adyen SDK 6 types are loose
        const checkout = await AdyenCheckout(checkoutConfig as any)
        checkoutRef.current = checkout
        // biome-ignore lint/suspicious/noExplicitAny: Adyen SDK 6 types are loose
        const card = new Card(checkout as any, cardConfig as any)
        card.mount(el)

        componentRef.current = card
        mountedElRef.current = el
        setStatus(AdyenCheckoutStatus.READY)
      })()

      try {
        await mountInFlightRef.current
      } finally {
        mountInFlightRef.current = null
      }
    },
    [
      ensureClients,
      ensureSessionResultPromise,
      options.adyenClientKey,
      options.adyenEnvironment,
      options.encodedSessionData,
      options.sessionType,
      options.showCardholderName,
      options.billingAddressMode,
      options.locale,
      onPaymentCompletedPayment,
      onPaymentCompletedTokenization,
      onPaymentFailed,
      onError,
      onChange,
      onFieldValid,
      onBrand,
    ]
  )

  // ----- submit -----

  const submit = useCallback(async (): Promise<SessionResult> => {
    const sessionId = sessionIdRef.current
    const component = componentRef.current as {
      state?: { isValid?: boolean; valid?: Record<string, boolean> }
      submit: () => void
    } | null

    const adyenClient = adyenClientRef.current
    if (!sessionId || !component || !adyenClient) {
      throw new Error('useAdyenCheckout.submit: component not mounted')
    }

    setStatus(AdyenCheckoutStatus.SUBMITTING)

    // Claim — may throw 409/410.
    await adyenClient.claimSession(sessionId)

    // Pre-submit guard (JET-386).
    if (!component.state?.isValid) {
      const error: ErrorWithState = new Error('Cannot submit session: component state is not valid')
      error.state = {
        isValid: component.state?.isValid,
        valid: component.state?.valid,
      }
      throw error
    }

    component.submit()

    // Wait for one of the Adyen callbacks to resolve the promise.
    const sessionResultPromise = waitForSessionResult()
    const result = await sessionResultPromise
    setStatus(result.refusal ? AdyenCheckoutStatus.FAILED : AdyenCheckoutStatus.COMPLETED)
    return result
  }, [waitForSessionResult])

  // SDK-native loading lock; FAILED releases it so a refused shopper can retry.
  useEffect(() => {
    const component = componentRef.current as { setStatus?: (status: string) => void } | null
    if (!component?.setStatus) return
    if (status === AdyenCheckoutStatus.SUBMITTING) {
      component.setStatus('loading')
    } else if (status === AdyenCheckoutStatus.READY || status === AdyenCheckoutStatus.FAILED) {
      component.setStatus('ready')
    }
  }, [status])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      const component = componentRef.current as { unmount?: () => void } | null
      if (component?.unmount) {
        try {
          component.unmount()
        } catch {
          // Already unmounted by a callback above — ignore.
        }
      }
    }
  }, [])

  return {
    status,
    mount,
    submit,
    applePayAvailable,
    mountApplePay,
    submitApplePay,
    googlePayAvailable,
    mountGooglePay,
  }
}
